import React, { FC, useCallback, useEffect, useState } from 'react'
import {
    AppState,
    AuthenticationState,
    BannerEvent,
    retrieveCookie,
    storeCookie,
    useFeature,
} from '@news-mono/web-common'
import {
    CREATE_ACCOUNT_COOKIE_NAME,
    CreateAccountBreachModalCookie,
    VERIFY_EMAIL_COOKIE_NAME,
    emailSentWithErrorNotification,
    emailSentWithSuccessNotification,
    getCookieExpiresInDay,
    storeCreateAccountModalCookie,
} from '..'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useAuthActions } from '../../authentication'
import { useSendVerifyEmail } from '../../../user-setting/hooks/useSendVerifyEmail'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StickyAccountInnerBanner } from './components/StickyAccountInnerBanner'
import {
    getSurveyLinks,
    handleDismissSurveyBanner,
    surveyBannerFeatureFlag,
    TN_USER_SURVEY_COOKIE_NAME,
    TNUserSurveyHelperBreachModalCookie,
    userSurveyMessages,
    viewSurveyBannerEvent,
} from '../__helpers/TNUserSurveyHelper'
import {
    DimensionProps,
    withDimensions,
} from '../../../__helpers/with-dimensions'

export interface StickyAccountBannerProps {
    isHidden: boolean
    message: string
    initialBannerText?: string
    existingBannerHeight?: number
    onBannerChange?: (bannerHeight: number) => void
    onEvent?: (event: BannerEvent) => void
    bannerHeight?: number
}
const queryClient = new QueryClient()

const defaultBannerText = 'Verify your email address.'

const excludedPages = {
    'create-account': ['account-deleted'],
    'verify-email': ['email-verified', 'manage-account', 'register-success'],
}

export const StickyAccountBannerWrapper = ({
    ...props
}: StickyAccountBannerProps) => {
    return (
        <QueryClientProvider client={queryClient}>
            <StickyAccountBanner {...props} />
        </QueryClientProvider>
    )
}

type InternalStickyAccountBannerProps = StickyAccountBannerProps &
    DimensionProps

type BannerState = 'CreateAccount' | 'VerifyAccount' | 'SendSurvey' | 'none'

export const InternalStickyAccountBanner: FC<InternalStickyAccountBannerProps> =
    ({
        isHidden,
        message,
        initialBannerText = 'Resend verification email.',
        onEvent,
        onBannerChange,
        existingBannerHeight,
        height,
        innerRef,
    }) => {
        const [bannerState, setBannerState] = useState<BannerState>('none')
        const [isDisplayed, setIsDisplayed] = useState<boolean>(false)
        const [bannerText, setBannerText] = useState<string>(defaultBannerText)

        const shouldDisplaySurveyBanner = useFeature(surveyBannerFeatureFlag)

        const { isLoggedIn, emailVerified, wanUserId, auth0UserId, isLoading } =
            useSelector<AppState, AuthenticationState>(
                (state) => state.authentication,
            )
        const location = useLocation()

        const showInThisPage = useCallback(() => {
            return !(
                !isLoggedIn
                    ? excludedPages['create-account']
                    : excludedPages['verify-email']
            ).some((page) => location.pathname.includes(page))
        }, [location.pathname, isLoggedIn])

        const userId = auth0UserId || wanUserId

        const { onRegisterClick } = useAuthActions()
        const { mutateAsync: sendEmail } = useSendVerifyEmail(userId)

        const onVerifyEmailClick = useCallback(async () => {
            const response = await sendEmail()
            if (response) {
                setBannerText(emailSentWithSuccessNotification)
            } else {
                setBannerText(emailSentWithErrorNotification)
            }
        }, [sendEmail])

        const handleCreateAccountPopupClose = () => {
            setIsDisplayed(false)

            const cookie = retrieveCookie<CreateAccountBreachModalCookie>(
                CREATE_ACCOUNT_COOKIE_NAME,
            )

            if (cookie) {
                storeCreateAccountModalCookie(true, new Date(cookie.expiresAt))
            }
        }

        const handleVerifyEmailPopupClose = () => {
            setIsDisplayed(false)

            const expires = getCookieExpiresInDay(1)
            storeCookie(VERIFY_EMAIL_COOKIE_NAME, expires, {
                expires: expires,
            })
        }

        const handleSendSurveyPopupClose = () => {
            setIsDisplayed(false)
            handleDismissSurveyBanner(userId, onEvent)
        }

        useEffect(() => {
            // It cannot be shown on this page, regardless of state so set displayed to false.
            if (!showInThisPage()) {
                setIsDisplayed(false)
                return
            }

            if (!isLoggedIn) {
                const cookie = retrieveCookie<CreateAccountBreachModalCookie>(
                    CREATE_ACCOUNT_COOKIE_NAME,
                )
                // hide banner if cookie is NOT found OR and stickyBannerIsDismissed is true
                if (!cookie || cookie.isStickyBannerIgnored) {
                    setIsDisplayed(false)
                } else {
                    setBannerState('CreateAccount')
                    setIsDisplayed(true)
                }
            } else if (!emailVerified) {
                const cookie = retrieveCookie<Date>(VERIFY_EMAIL_COOKIE_NAME)

                if (cookie === undefined) {
                    setBannerText(defaultBannerText)

                    setBannerState('VerifyAccount')
                    setIsDisplayed(true)
                } else {
                    setIsDisplayed(false)
                }
            } else {
                const cookie =
                    retrieveCookie<TNUserSurveyHelperBreachModalCookie>(
                        TN_USER_SURVEY_COOKIE_NAME,
                    )

                if (cookie === undefined && shouldDisplaySurveyBanner) {
                    setBannerState('SendSurvey')
                    setIsDisplayed(true)
                } else {
                    setBannerState('none')
                    setIsDisplayed(false)
                }
            }
        }, [
            isLoggedIn,
            emailVerified,
            location,
            showInThisPage,
            shouldDisplaySurveyBanner,
        ])

        useEffect(() => {
            if (isDisplayed && !isLoading && bannerState === 'SendSurvey') {
                viewSurveyBannerEvent(userId, onEvent)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isDisplayed, isLoading, bannerState, userId])

        useEffect(() => {
            if (onBannerChange) {
                if (!isDisplayed || isLoading) {
                    if (existingBannerHeight !== 0) {
                        onBannerChange(0)
                    }
                } else {
                    if (existingBannerHeight !== height) {
                        onBannerChange(height)
                    }
                }
            }
        }, [
            existingBannerHeight,
            height,
            isDisplayed,
            isLoading,
            onBannerChange,
        ])

        const renditionType =
            useSelector((state: AppState) => state.render.renditionType) ===
            'app'
                ? 'app'
                : 'web'

        if (!isDisplayed || isLoading) {
            return null
        }

        switch (bannerState) {
            case 'CreateAccount': {
                const links = [
                    {
                        linkText: 'Create free account.',
                        onLinkClick: () =>
                            onRegisterClick('header-banner', renditionType),
                    },
                ]
                return (
                    <StickyAccountInnerBanner
                        isHidden={isHidden}
                        messages={[message]}
                        links={links}
                        onClose={handleCreateAccountPopupClose}
                        ref={innerRef}
                    />
                )
            }
            case 'VerifyAccount': {
                const links =
                    bannerText === emailSentWithSuccessNotification
                        ? []
                        : [
                              {
                                  linkText: initialBannerText,
                                  onLinkClick: onVerifyEmailClick,
                              },
                          ]

                return (
                    <StickyAccountInnerBanner
                        isHidden={isHidden}
                        messages={[bannerText]}
                        links={links}
                        onClose={handleVerifyEmailPopupClose}
                        ref={innerRef}
                    />
                )
            }
            case 'SendSurvey': {
                viewSurveyBannerEvent(userId, onEvent)
                const closeHandler = () => setIsDisplayed(false)
                return (
                    <StickyAccountInnerBanner
                        isHidden={isHidden}
                        messages={userSurveyMessages}
                        links={getSurveyLinks(userId, closeHandler, onEvent)}
                        onClose={handleSendSurveyPopupClose}
                        ref={innerRef}
                    />
                )
            }

            case 'none':
                return null
        }
    }

export const StickyAccountBanner = withDimensions<StickyAccountBannerProps>(
    InternalStickyAccountBanner,
)
StickyAccountBanner.displayName = 'StickyAccountBanner'
